import styled from 'styled-components'
import { LayoutComponent } from '@/layout'
import GreenCheck from '../../assets/img/green-check.png'
import { createSearchParams, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { trackEvent } from '@/services/analytics.service'
import { getPrescriptionById } from '@/services/api.service'
import { LoadingComponent } from '@/components/loading/loading.component'

const PageStyle = styled.section`
  .check-container {
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 300px;
    margin: 0 auto 16px;
    text-align: center;
    img {
      max-width: 100px;
    }
  }

  .response-container {
    p {
      margin: 1rem .5rem;
    }
    ul {
      font-weight: bold;
      padding: .5rem;
    }
  }

  @media print {
    button {
      display: none;
    }
  }

  #ticket {
    margin-bottom: 2rem;
  }
`

interface Ticket {
  pharmacy: string
  address: string
  code: string
  items: string[]
}

/**
 * Datos del ticket en el query string. Es el formato antiguo de `/success`, que
 * se mantiene para enlaces ya repartidos: la app ya no genera esas URLs, ahora
 * navega a `/:prescriptionId/success` y los datos se piden al backend.
 */
function ticketFromQuery (searchParams: URLSearchParams): Ticket {
  return {
    pharmacy: searchParams.get('pharmacy') ?? '',
    address: searchParams.get('address') ?? '',
    code: searchParams.get('code') ?? '',
    items: searchParams.getAll('items')
  }
}

export function SuccessPage (): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const ticketRef = useRef<HTMLDivElement>(null)
  const [ticket, setTicket] = useState<Ticket | null>(id != null ? null : ticketFromQuery(searchParams))

  useEffect(() => {
    if (id == null) {
      return
    }

    async function loadTicket (prescriptionId: string): Promise<void> {
      try {
        const response = await getPrescriptionById({ prescriptionId })

        if (!('bookingId' in response)) {
          // La receta existe pero aún no está reservada: no hay ticket que dar.
          navigate(`/${prescriptionId}`, { replace: true })
          return
        }

        const [pharmacy] = response.pharmacies

        setTicket({
          pharmacy: pharmacy.pharmacy.description,
          address: pharmacy.pharmacy.address,
          code: response.bookingId,
          items: pharmacy.items.map((item) => item.description ?? item.code)
        })
      } catch (error: any) {
        const message = error.response?.data?.message ?? 'No se ha podido recuperar la reserva'

        navigate({
          pathname: `/${prescriptionId}/error`,
          search: createSearchParams({ message }).toString()
        }, { replace: true })
      }
    }

    void loadTicket(id)
  }, [id, navigate])

  const itemsCount = ticket?.items.length

  useEffect(() => {
    if (itemsCount == null) {
      return
    }

    trackEvent('booking_confirmation_view', {
      items_count: itemsCount,
      source: id != null ? 'prescription_id' : 'query_string'
    })
  }, [itemsCount, id])

  if (ticket == null) {
    return (
      <LayoutComponent>
        <LoadingComponent />
      </LayoutComponent>
    )
  }

  const intialText = (): string => ticket.items.length === 0 ? 'Tu medicamento ha sido localizado con éxito.' : 'Tus medicamentos han sido localizados con éxito.'

  const generateTicket = async (): Promise<void> => {
    trackEvent('ticket_print', { items_count: ticket.items.length })
    window.print()
  }

  return (
    <LayoutComponent>
      <PageStyle>
        <div id='ticket' ref={ticketRef}>
          <div className='check-container'>
            <img src={GreenCheck} alt='' />
          </div>
          <div className='response-container'>
            <p>
              {intialText()}
            </p>
            <p>
              Puede recoger sus medicamentos en la <span>Farmacia</span>: <b>{ticket.pharmacy}</b>
            </p>
            <p>
              <span>Medicamentos: </span>
              <ul>
                {ticket.items.map((item, index) => (
                  <li key={index}>- {item}</li>
                ))}
              </ul>
            </p>
            <p>
              <span>Dirección: </span> <b>{ticket.address}</b>
            </p>
            <p>
              <span>Código a presentar: </span> <b>{ticket.code}</b>
            </p>
            <p>
              No olvide llevar la receta en el momento de la recogida en la
              farmacia.
            </p>
            <p>
              ¡Gracias por utilizar el servicio de localización de
              medicamentos en farmacia!
            </p>
            <Button onClick={generateTicket as any}>
              Guardar Ticket
            </Button>
          </div>
        </div>
      </PageStyle>
    </LayoutComponent>
  )
}
